import React, { useCallback, useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../theme";
import {
  getVendorOverviewAction,
  getVendorProfileAction
} from "../../containers/vendorDashboard/action";
import { moneyValue, getStartEndDate } from "../../utils";
import Loading from "../loading";
import DragDatePreviewer from "../datepicker/dragDatePickerPreviewer";


const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 185px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  headerContainer: {
    marginBottom: "30px",
    "& .title": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "24px",
    },
  },
  summaryCard: {
    backgroundColor: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "10px",
    padding: "30px 40px",
    marginBottom: "20px",
    "& .cardTitle": {
      color: theme.colors.Violet,
      fontSize: "18px",
      fontFamily: theme.fontFamily.MARKPROBOLD,
      marginBottom: "20px",
    },
    "& .summaryRow": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px 0",
      borderBottom: `1px solid ${theme.colors.Zumthor}`,
      "&:last-child": {
        borderBottom: "none",
      },
      "& .label": {
        color: theme.colors.Violet,
        fontSize: "14px",
        fontFamily: theme.fontFamily.MARKPRO,
      },
      "& .value": {
        color: theme.colors.Violet,
        fontSize: "18px",
        fontFamily: theme.fontFamily.MARKPROHEAVY,
        "& .currency": {
          fontSize: "12px",
          position: "relative",
          bottom: "3px",
        },
      },
    },
  },
  periodSelector: {
    display: "flex",
    gap: "15px",
    marginBottom: "30px",
    "& .periodButton": {
      padding: "10px 20px",
      borderRadius: "21px",
      border: `1px solid ${theme.colors.DodgerBlue}`,
      backgroundColor: theme.colors.White,
      color: theme.colors.DodgerBlue,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "14px",
      cursor: "pointer",
      "&.active": {
        backgroundColor: theme.colors.DodgerBlue,
        color: theme.colors.White,
      },
    },
  },
}));

function VendorReports() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const getVendorOverviewMethod = useCallback(
    (data) => dispatch(getVendorOverviewAction(data)),
    [dispatch]
  );

  const getVendorProfileMethod = useCallback(
    (data) => dispatch(getVendorProfileAction(data)),
    [dispatch])

  const vendorDashboardReducer = useSelector((state) => state.vendorDashboardReducer);

  const [selectedPeriod, setSelectedPeriod] = useState("today"); // today, week, month
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [start_date, end_date] = getStartEndDate();
  const [defaultStartDate, setDefaultStartDate] = useState(
    start_date.toISOString().split("T")[0]
  );
  const [defaultEndDate, setDefaultEndDate] = useState(
    end_date.toISOString().split("T")[0]
  );

  useEffect(() => {
    getVendorProfileMethod();
  }, [getVendorProfileMethod]);

  const vendorProfile = vendorDashboardReducer?.vendorProfile;

  useEffect(() => {
    let filters = {};

    if (selectedPeriod === "today") {
      const today = new Date();
      const start = new Date(today);
      start.setUTCHours(0, 0, 0, 0);
      filters.start_date = start.toISOString().split("T")[0];
      const end = new Date(today);
      end.setUTCHours(23, 59, 59, 999);
      filters.end_date = end.toISOString().split("T")[0];
    } else if (selectedPeriod === "week") {
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      weekStart.setUTCHours(0, 0, 0, 0);
      filters.start_date = weekStart.toISOString().split("T")[0];
      const end = new Date(today);
      end.setUTCHours(23, 59, 59, 999);
      filters.end_date = end.toISOString().split("T")[0];
    } else if (selectedPeriod === "month") {
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      monthStart.setUTCHours(0, 0, 0, 0);
      filters.start_date = monthStart.toISOString().split("T")[0];
      const end = new Date(today);
      end.setUTCHours(23, 59, 59, 999);
      filters.end_date = end.toISOString().split("T")[0];
    } else if (selectedPeriod === "custom" && startDate && endDate) {
      const start = new Date(startDate);
      start.setUTCHours(0, 0, 0, 0);
      filters.start_date = start.toISOString().split("T")[0];
      const end = new Date(endDate);
      end.setUTCHours(23, 59, 59, 999);
      filters.end_date = end.toISOString().split("T")[0];
    }
    if (vendorProfile?.vendor?.id) {
      getVendorOverviewMethod({ vendor_id: vendorProfile?.vendor?.id, ...filters });
    }
  }, [selectedPeriod, startDate, endDate, getVendorOverviewMethod, vendorProfile?.vendor?.id]);

  const overview = vendorDashboardReducer?.vendorOverview;

  return (
    <div className={classes.root}>
      <Loading showLoader={vendorDashboardReducer?.loading} />
      {vendorDashboardReducer?.error?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.error)
            ? vendorDashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.error}</div>}
        </div>
      ) : null}

      <div className={classes.headerContainer}>
        <div className="title">Sales & Reports</div>
      </div>

      <div className={classes.periodSelector}>
        <div
          className={`periodButton ${selectedPeriod === "today" ? "active" : ""}`}
          onClick={() => setSelectedPeriod("today")}
        >
          Today
        </div>
        <div
          className={`periodButton ${selectedPeriod === "week" ? "active" : ""}`}
          onClick={() => setSelectedPeriod("week")}
        >
          This Week
        </div>
        <div
          className={`periodButton ${selectedPeriod === "month" ? "active" : ""}`}
          onClick={() => setSelectedPeriod("month")}
        >
          This Month
        </div>
        <div
          className={`periodButton ${selectedPeriod === "custom" ? "active" : ""}`}
          onClick={() => setSelectedPeriod("custom")}
        >
          Custom Range
        </div>
      </div>

      {selectedPeriod === "custom" && (
        <div style={{ marginBottom: "30px" }}>
          <DragDatePreviewer
            startDateValue={startDate || defaultStartDate}
            setStartDateValue={(date) => {
              setStartDate(new Date(date).toISOString().split("T")[0]);
            }}
            endDateValue={endDate || defaultEndDate}
            setEndDateValue={(date) => {
              setEndDate(new Date(date).toISOString().split("T")[0]);
            }}
            absoluteCalendar
          />
        </div>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <div className={classes.summaryCard}>
            <div className="cardTitle">Transaction Summary</div>
            <div className="summaryRow">
              <div className="label">Total Transactions</div>
              <div className="value">
                {overview?.payment_count || 0}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">Total Sales Volume</div>
              <div className="value">
                <span className="currency">
                  {overview?.currency || "NGN"}
                </span>
                {moneyValue(overview?.total_amount_received || 0)}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">Total Discounts Applied</div>
              <div className="value">
                <span className="currency">
                  {overview?.currency || "NGN"}
                </span>
                {moneyValue(overview?.settlement_charge || 0)}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">Net Earnings</div>
              <div className="value">
                <span className="currency">
                  {overview?.currency || "NGN"}
                </span>
                {moneyValue(overview?.settlement_net || 0)}
              </div>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className={classes.summaryCard}>
            <div className="cardTitle">Performance Indicators</div>
            <div className="summaryRow">
              <div className="label">Average Transaction Value</div>
              <div className="value">
                <span className="currency">
                  {overview?.currency || "NGN"}
                </span>
                {overview?.payment_count > 0
                  ? moneyValue(
                    (overview?.total_amount_received || 0) /
                    overview.payment_count
                  )
                  : moneyValue(0)}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">Discount Rate</div>
              <div className="value">
                {overview?.total_amount_received > 0
                  ? (
                    ((overview?.settlement_charge || 0) /
                      overview.total_amount_received) *
                    100
                  ).toFixed(2) + "%"
                  : "0%"}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">Active Customers</div>
              <div className="value">
                {overview?.active_customers_count || 0}
              </div>
            </div>
            <div className="summaryRow">
              <div className="label">New vs Returning</div>
              <div className="value">
                {overview?.new_employees_count || 0} /{" "}
                {overview?.returning_employees_count || 0}
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

export default VendorReports;
